import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { initializeExchangeRates, CURRENCY_LIST_KEY } from '../utils/currency';

const CurrencyContext = createContext();
const API_BASE = (() => {
  const b = import.meta.env.VITE_URL || import.meta.env.VITE_URL2 || 'http://localhost:3000';
  return typeof b === 'string' ? b.replace(/\/api\/activity\/?$/, '') : 'http://localhost:3000';
})();

export const useCurrency = () => {
  const context = useContext(CurrencyContext);
  if (!context) {
    throw new Error('useCurrency must be used within CurrencyProvider');
  }
  return context;
};

export const CurrencyProvider = ({ children }) => {
  const [currency, setCurrency] = useState(() => {
    try {
      const saved = localStorage.getItem('selected_currency');
      return saved || 'INR';
    } catch {
      return 'INR';
    }
  });

  const [currencies, setCurrencies] = useState([]);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await fetch(`${API_BASE}/api/activity/user/currencies`);
        const data = await res.json();
        const list = data?.data || [];
        if (Array.isArray(list) && list.length) {
          setCurrencies(list);
          try {
            localStorage.setItem(CURRENCY_LIST_KEY, JSON.stringify(list));
          } catch {}
        }
      } catch (err) {
        console.warn('Failed to fetch currencies:', err);
        const def = [{ code: 'INR', name: 'Indian Rupee', symbol: '₹', multiplier: 1 }, { code: 'USD', name: 'US Dollar', symbol: '$', multiplier: 0.012 }, { code: 'EUR', name: 'Euro', symbol: '€', multiplier: 0.011 }];
        setCurrencies(def);
        try {
          localStorage.setItem(CURRENCY_LIST_KEY, JSON.stringify(def));
        } catch {}
      }
    };
    fetchCurrencies();
  }, []);

  useEffect(() => {
    const init = async () => {
      try {
        await initializeExchangeRates();
        setIsInitialized(true);
      } catch (error) {
        console.warn('Failed to initialize exchange rates:', error);
        setIsInitialized(true); // Still mark as initialized to allow app to function
      }
    };
    init();
  }, []);

  // Save currency to localStorage when it changes
  useEffect(() => {
    try {
      localStorage.setItem('selected_currency', currency);
    } catch (error) {
      console.warn('Failed to save currency preference:', error);
    }
  }, [currency]);

  const changeCurrency = useCallback((newCurrency) => {
    if (newCurrency && typeof newCurrency === 'string') {
      setCurrency(newCurrency.trim().toUpperCase() || 'INR');
    }
  }, []);

  const value = {
    currency,
    changeCurrency,
    currencies,
    isInitialized,
  };

  return (
    <CurrencyContext.Provider value={value}>
      {children}
    </CurrencyContext.Provider>
  );
};
